import type React from 'react';
import type { FocusEvent, KeyboardEvent, ReactNode } from 'react';
import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import { debounce } from 'lodash-es';
import type { PopupActions, PopupProps } from 'reactjs-popup/dist/types';

import {
  ContextMenuIcon,
  ErrorIcon,
  WarningIcon,
} from '@polyai/ui/components/atoms/Icons';
import type { BaseIcon } from '@polyai/ui/components/atoms/Icons/BaseIcon';
import Tooltip from '@polyai/ui/components/atoms/Tooltip';
import type { ColorName } from '@polyai/ui/styles/config/colors';

import * as Styled from './ActionTag.styled';

export interface ActionTagRef {
  openPopup: () => void;
}

interface ActionTagProps {
  end: number;
  onClose?: () => void;
  onOpen?: () => void;
  onFocus?: (e: FocusEvent<HTMLSpanElement>) => void;
  onBlur?: (e: FocusEvent<HTMLSpanElement>) => void;
  setCursorPosition: (num: number) => void;
  start: number;
  warning?: string;
  error?: string;
  disabled?: boolean;
  isStatic?: boolean;
  loading?: boolean;
  action: ReactNode;
  children: ReactNode | ((values: { closePopup?: () => void }) => ReactNode);
  Icon: typeof BaseIcon;
  backgroundColor?: string;
  iconColor?: ColorName;
  textColor?: ColorName;
  isSelected?: boolean;
  tabIndex?: number;
  readonly?: boolean;
}

const ActionTag = forwardRef<ActionTagRef, ActionTagProps>(
  (
    {
      end,
      onClose,
      onOpen,
      onFocus,
      onBlur,
      setCursorPosition,
      start,
      action,
      warning,
      error,
      disabled,
      isStatic,
      loading,
      children,
      Icon,
      backgroundColor,
      iconColor,
      textColor,
      isSelected,
      tabIndex = 0,
    },
    ref,
  ) => {
    const [open, setOpen] = useState(false);
    const popupRef = useRef<PopupActions>(null);
    const functionRef = useRef<HTMLSpanElement>(null);
    const [isOverflow, setIsOverflow] = useState<boolean>(false);
    const handleKeyDown = useCallback(
      (e: KeyboardEvent<HTMLSpanElement>) => {
        switch (e.key) {
          case 'Enter':
          case 'Space':
            popupRef.current?.open();
            e.stopPropagation();
            e.preventDefault();
            return;
          case 'ArrowLeft':
            setCursorPosition(start);
            e.stopPropagation();
            e.preventDefault();
            return;
          case 'ArrowRight':
            setCursorPosition(end + 1);
            e.stopPropagation();
            e.preventDefault();
            return;
        }
        e.stopPropagation();
      },
      [end, setCursorPosition, start],
    );

    const handleOpen = useCallback(() => {
      setOpen(true);
      onOpen?.();
    }, [onOpen]);

    const handleClose = useCallback(
      (
        event: React.SyntheticEvent | KeyboardEvent | TouchEvent | MouseEvent,
      ) => {
        setOpen(false);
        onClose?.();
        if (event && 'key' in event && tabIndex !== -1) {
          functionRef.current?.focus();
        }
      },
      [onClose, tabIndex],
    ) as NonNullable<PopupProps['onClose']>;

    const debouncedSetIsValueOverflow = useMemo(
      () => debounce(setIsOverflow, 100),
      [],
    );

    const actionNameRef = (element: HTMLSpanElement | null) => {
      if (element) {
        debouncedSetIsValueOverflow(
          element.offsetWidth > Styled.TAG_BOX_MAX_WIDTH,
        );
      }
    };

    useImperativeHandle(ref, () => ({
      openPopup: () => {
        popupRef.current?.open();
        handleOpen();
      },
    }));

    return (
      <Styled.ActionTagContainer contentEditable={false}>
        {(warning || error) && (
          <Styled.TagBox
            $backgroundColor={backgroundColor}
            $disabled={disabled || loading}
            data-test-id="action-tag-warning"
            $iconOnly
          >
            <Tooltip content={warning || error} asChild>
              <span>
                {warning ? (
                  <WarningIcon
                    color={disabled || loading ? 'iconDisabled' : 'iconWarning'}
                    style={{
                      verticalAlign: 'middle',
                    }}
                    customFill
                  />
                ) : (
                  <ErrorIcon
                    color={disabled || loading ? 'iconDisabled' : 'iconDanger'}
                    style={{
                      verticalAlign: 'middle',
                    }}
                    customFill
                  />
                )}
              </span>
            </Tooltip>
          </Styled.TagBox>
        )}

        <Tooltip content={action} disabled={!isOverflow} inline>
          <Styled.InlinePopup
            isDisabled={loading || isStatic}
            offsetY={8}
            popupRef={popupRef}
            position="bottom left"
            trigger={
              <Styled.TagBoxWrapper
                $backgroundColor={backgroundColor}
                $borderColor={iconColor}
                $isSelected={isSelected}
                $isStatic={isStatic}
                $open={open}
              >
                <Styled.TagBox
                  ref={functionRef}
                  $backgroundColor={backgroundColor}
                  $isOverflow={isOverflow}
                  $isStatic={isStatic}
                  aria-label={typeof action === 'string' ? action : undefined}
                  data-test-id="action-tag"
                  id={`action-tag-${start}-${end}`}
                  role="button"
                  tabIndex={tabIndex}
                  onBlur={onBlur}
                  onFocus={onFocus}
                  onKeyDown={handleKeyDown}
                >
                  <Icon
                    color={
                      loading ? 'iconDisabled' : (iconColor ?? 'iconAccent')
                    }
                  />
                  <Styled.ActionName ref={actionNameRef} $color={textColor}>
                    {action}
                  </Styled.ActionName>
                  {!isStatic && <ContextMenuIcon color={iconColor} />}
                </Styled.TagBox>
              </Styled.TagBoxWrapper>
            }
            keepTooltipInside
            nested
            onClose={handleClose}
            onOpen={handleOpen}
          >
            {typeof children === 'function'
              ? children({ closePopup: popupRef.current?.close })
              : children}
          </Styled.InlinePopup>
        </Tooltip>
      </Styled.ActionTagContainer>
    );
  },
);

export default ActionTag;
